/**
 * The verse module's data layer. Everything goes through `api` (src/lib/api.ts) — no raw
 * fetch anywhere, so a 403 for a kid arrives as an ApiRequestError the UI can read rather
 * than as a crash.
 */
import { api, ApiRequestError } from '../../lib/api';
import type {
  BibleTranslation,
  VerseConfig,
  VerseOfWeek,
  VersePlanEntry,
} from '../../shared/types';

export const getVerse = () => api.get<VerseOfWeek | null>('/verse');
export const getConfig = () => api.get<VerseConfig>('/verse/config');
export const getTranslations = () => api.get<BibleTranslation[]>('/verse/translations');
export const putConfig = (bible_id: string) => api.put<VerseConfig>('/verse/config', { bible_id });
export const postRefresh = () => api.post<VerseOfWeek>('/verse/refresh');

export const getPlan = () => api.get<VersePlanEntry[]>('/verse/plan');
export const addEntry = (reference: string, note: string | null) =>
  api.post<VersePlanEntry>('/verse/plan', { reference, note });
export const editEntry = (id: number, reference: string, note: string | null) =>
  api.put<VersePlanEntry>(`/verse/plan/${id}`, { reference, note });
export const removeEntry = (id: number) => api.del<VersePlanEntry>(`/verse/plan/${id}`);
export const reorderPlan = (ids: number[]) =>
  api.put<VersePlanEntry[]>('/verse/plan/reorder', { ids });

/**
 * Monday of the current week. Used for ONE question: is the cached verse this week's, or is
 * it from an earlier week because a Monday refresh didn't land? It answers WHETHER the verse
 * is stale, never HOW stale — the row's own `week_start` is the only thing allowed to say
 * that. The Worker owns the real week boundary (household timezone); this only decides
 * whether the card admits staleness, so erring toward "say it's stale" is the safe direction.
 */
export function thisMonday(now = new Date()): string {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Mon = 0
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 'YYYY-MM-DD' → 'July 13' — or 'July 13, 2025' when it isn't this year. Parsed as a local
 * date, never as UTC midnight. A verse cached before a long outage can be months or years
 * old; a bare 'July 13' would quietly read as this July.
 */
export function prettyDate(iso: string, now = new Date()): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    ...(y === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * The API's own words when it has them. Never invent a cause — an unexplained failure says
 * so plainly instead of guessing at a reason we haven't established.
 */
export function explain(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) {
    if (err.body.detail) return err.body.detail;
    if (err.status === 403) return 'Only a grown-up can do that.';
    if (err.status >= 500) return 'The API returned an error. Try again in a moment.';
  }
  return fallback;
}
